import React, { useContext, useEffect, useMemo, useState } from 'react';
import phone from "../../../images/phone.svg";
import {ReactSVG} from "react-svg";
import "./VerificationComponentStyles.scss";
import {Button} from "../../../components/Button";
import {Field, FieldProps, Formik} from "formik";
import {TextField} from "@material-ui/core";
import {OutputLink} from "../../../components/OutputLink";
import config from "../../../config";
import {sendIdentificationCode, signQuotation} from "../../../api/identificationApi";
import {QuotationContext} from "../../../contexts/QuotationContext";
import {ScenarioContext} from "../../../contexts/ScenarioContext";
import {Quotation} from "../../../model/quotation";
import Link from "@material-ui/core/Link";
import InputMask from "react-input-mask";
import Typography from "@material-ui/core/Typography";
import {showOrNotTechnicalWorkPage} from "../../MessagePage";
import {strings} from "../../../strings/strings";
import {isSmartDrive} from "../../../utils/quotation";
import {ProcessingContext} from "../../../contexts/ProcessingContext";
import {getSnippetByName} from "../../../api/snippetApiClient";
import {WIDGET_DURATION, WidgetTooltip} from "../../../App";
import {ChatWidget} from "../../../model/chatWidget";

interface VerificationComponentProps {
    onVerify: (quotation: Quotation, paymentPageUrl: string) => void
    title: string
}

export const VerificationComponent = ({onVerify, title}: VerificationComponentProps) => {
    const [verification, setVerification] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(config.verification.resendTimeout);
    const [controlCode, setControlCode] = useState();
    const [mtsbuLink, setMtsbuLink] = useState("");
    const {quotation, setIsQuotationEditable, tarrificationProgram} = useContext(QuotationContext);
    const {currentStep} = useContext(ScenarioContext);
    const [errorMessageVerifyCode, setErrorMessageVerifyCode] = useState("");
    const {setProcessingOpen} = useContext(ProcessingContext);
    const {type} = useContext(QuotationContext);

    useEffect(() => {
        if (!verification) return;

        const timer = secondsLeft > 0 && setInterval(() => setSecondsLeft(secondsLeft - 1), 1000);
        return () => clearInterval(timer);
    }, [verification, secondsLeft]);

    React.useEffect(() => {
        return () => WidgetTooltip?.close()
    }, [])

    const sendCode = async () => {
        if (showOrNotTechnicalWorkPage()) return;

        setProcessingOpen(true);
        setErrorMessageVerifyCode("");

        showWidgetTooltip(ChatWidget.TEMPLATE_HINT_SMS, 500);

        try {
            const value = await sendIdentificationCode(quotation.id, type);
            handleSuccessfulCodeSent(value);
        } catch (error) {
            setProcessingOpen(false);
        }
    };

    const showWidgetTooltip = (snippetName: string, delay: number) => {
        getSnippetByName(snippetName).then(res => {
            setTimeout(() => WidgetTooltip?.open(res.text, WIDGET_DURATION), delay);
        });
    };

    const handleSuccessfulCodeSent = (value: any) => {
        setControlCode(value.controlCode);
        setMtsbuLink(value.mtsbuLink);
        setVerification(true);
        setSecondsLeft(config.verification.resendTimeout);
        setProcessingOpen(false);
        setIsQuotationEditable(false);

        WidgetTooltip?.close();
        showWidgetTooltip(ChatWidget.TEMPLATE_HINT_PAYMENT, 1000);
    };

    const verifyCode = (otpCode: string) => {
        if (showOrNotTechnicalWorkPage(true)) return;
        setProcessingOpen(true);
        setErrorMessageVerifyCode("");
        signQuotation(quotation.id, currentStep.code, otpCode, type).then(value => {
            if (value.error === true) {
                const errorMessage = new RegExp(/Error:\s+([^;]*)/).exec(value.errorMessage)?.[1];
                const message = errorMessage || strings.unkwonwnError;
                setErrorMessageVerifyCode(message);
                setProcessingOpen(false);
            } else {
                onVerify(value.quotation, value.paymentPageUrl);
                setProcessingOpen(false);
            }
        });
    };

    const getPrintoutUrl = (contractType: string) => {
        return `${config.API_URL}/mtpl/printout?quotationId=${quotation.id}&contractType=${contractType}`;
    }

    const sendAgainBtnLbl = useMemo(() => {
        if (secondsLeft > 0) return `${strings.sendAgain} (${secondsLeft})`;
        return strings.sendAgain;
    }, [secondsLeft]);

    return (
        <div className="verification-component">
            <div className={'verification-component__header'}>
                <div className={"verification-component__header__icon"}>
                    <ReactSVG src={phone}/>
                </div>
                <div className={"verification-component__header__text"}>
                    {strings.verifyPhone}
                </div>
            </div>
            <div className={'verification-component__body'}>

                {!verification && (
                    <div className={"verification-component__body__pre-verification"}>
					<span className={"verification-component__body__pre-verification__message"}>
						{strings.verifyDescription1}
                        <span className={"verification-component__body_accent verification-component__body_bold"}>
							{quotation.mtplContract.primaryInsured.mobileNumber}
						</span>
                        {strings.verifyDescription2}</span>
                        <Button id={'getCodeBtn'} label={strings.getCode} onClickListener={() => sendCode()}/>
                    </div>
                )}

                {verification && (
                    <div className={"verification-component__body__verification"}>

                        {quotation.cascoAdditionalCoverage.covered && quotation.vtplAdditionalCoverage.covered && (
                            <span className={"verification-component__body__verification__message"}>
								<span className={"verification-component__body__pre-verification__message"}>
									{strings.preVerificationMessageReadMany}
                                    <a id={'verificationComponentBodyLinkCasco'}
                                       className={'verification-component__body_accent verification-component__body--link'}
                                       href={getPrintoutUrl('casco')}
                                       target="_blank"
                                       rel="noopener noreferrer">
                                           {isSmartDrive(tarrificationProgram) ?
                                            strings.preVerificationMessageCascoSmartDrive
                                            :
                                            strings.preVerificationMessageCasco
                                           }
                                    </a>
                                    {strings.and}
                                    <a id={'verificationComponentBodyLinkAgo'}
                                       className={'verification-component__body_accent verification-component__body--link'}
                                       href={getPrintoutUrl('dgo')}
                                       target="_blank"
                                       rel="noopener noreferrer">{strings.preVerificationMessageVtpl}</a>
                                    {strings.preVerificationMessageSign}
								</span>
							</span>
                        )}
                        {quotation.cascoAdditionalCoverage.covered && !quotation.vtplAdditionalCoverage.covered && (
                            <span className={"verification-component__body__verification__message"}>
								<span className={"verification-component__body__pre-verification__message"}>
									{strings.preVerificationMessageReadOne}
                                    <a id={'verificationComponentBodyLinkCasco'}
                                       className={'verification-component__body_accent verification-component__body--link'}
                                       href={getPrintoutUrl('casco')}
                                       target="_blank"
                                       rel="noopener noreferrer">
                                           {isSmartDrive(tarrificationProgram) ?
                                            strings.preVerificationMessageCascoSmartDrive
                                            :
                                            strings.preVerificationMessageCasco
                                           }
                                    </a>
                                    {strings.preVerificationMessageSign}
								</span>
							</span>
                        )}
                        {!quotation.cascoAdditionalCoverage.covered && quotation.vtplAdditionalCoverage.covered && (
                            <span className={"verification-component__body__verification__message"}>
								<span className={"verification-component__body__pre-verification__message"}>
									{strings.preVerificationMessageReadOne}
                                    <a id={'verificationComponentBodyLinkAgo'}
                                       className={'verification-component__body_accent verification-component__body--link'}
                                       href={getPrintoutUrl('dgo')}
                                       target="_blank"
                                       rel="noopener noreferrer">{strings.preVerificationMessageVtpl}</a>
                                    {strings.preVerificationMessageSign}
								</span>
							</span>
                        )}
                        <div className={"verification-component__body__verification__submit-block"}>
                            <Formik initialValues={{
                                code: "W"
                            }} onSubmit={values => {
                                verifyCode(values.code);
                            }}>
                                {formProps => (
                                    <form onSubmit={formProps.handleSubmit}>
                                        <div
                                            className={"verification-component__body__verification__submit-block__code-input"}>
                                            <div
                                                className={"verification-component__body__verification__submit-block__code-input__input"}>
                                                <Field name={"code"}>
                                                    {({field, form, meta}: FieldProps) => (
                                                        <InputMask
                                                            {...field}
                                                            mask={"W99999"}
                                                            maskChar={"_"}
                                                            alwaysShowMask={true}>
                                                            {
                                                                //@ts-ignore
                                                            (field: any) => (
                                                                <TextField {...field}
                                                                           fullWidth={true}
                                                                           helperText={
                                                                               errorMessageVerifyCode !== '' ? errorMessageVerifyCode : `${strings.controlCode} ${controlCode}`
                                                                           }
                                                                           error={errorMessageVerifyCode !== ''}
                                                                           variant="outlined"
                                                                           label={strings.code}
                                                                           InputProps={{
                                                                               ...field.InputProps,
                                                                               shrink: true,
                                                                           }}/>)}
                                                        </InputMask>
                                                    )}
                                                </Field>
                                            </div>

                                            <div
                                                className={"verification-component__body__verification__submit-block__code-input__button"}>
                                                <Button id={'confirmOrderBtn'}
                                                        label={strings.confirm}
                                                        onClickListener={() => formProps.submitForm()}/>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </Formik>
                        </div>

                        <div className={"verification-component__body__verification__resend"}>
                            <OutputLink label={sendAgainBtnLbl}
                                        onClickListener={() => {
                                            sendCode();
                                        }}
                                        variant={"grey"}
                                        uppercase={true}
                                        disabled={secondsLeft > 0}/>
                        </div>

                        <div className={"verification-component__body__verification__resend"}>
                            <Typography>
                                <Link href={mtsbuLink}
                                      target={"_blank"}
                                      variant={"body1"}
                                      color={"error"}
                                      underline={"always"}>
                                    {title}
                                </Link>
                            </Typography>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};
